import { MailingList } from "./api/types";
import {
  MailingListSubscription,
  SubscriptionCategory,
  CategoryConfig,
} from "./types";

// Audience key mapping from legacy article IDs to new API keys
export const AUDIENCE_KEY_MAP: Record<string, string> = {
  "9": "daily_us_earnings",
  "2": "daily_economic_releases",
  "29": "daily_european_opening",
  "30": "daily_european_equity",
  "31": "daily_us_opening",
  "10": "week_ahead",
  "11": "weekly_economic_calendar",
  "23": "weekly_us_earnings",
  "12": "adhoc_economic_analysis",
  "26": "calendar_key_events",
  "28": "options_expiry",
  "25": "central_banks",
  "32": "daily_us_equity_opening",
  "33": "bond_auction_previews",
  "34": "us_market_wrap",
  "37": "daily_asia_pac_opening",
  "40": "daily_us_conference_calendar",
};

// Reverse mapping for API responses
export const ID_FROM_AUDIENCE_KEY: Record<string, string> = Object.fromEntries(
  Object.entries(AUDIENCE_KEY_MAP).map(([id, key]) => [key, id])
);

// Category configuration
export const CATEGORY_CONFIG: Record<SubscriptionCategory, CategoryConfig> = {
  daily: {
    label: "Daily Reports",
    description: "Delivered every trading day to keep you informed",
    order: 1,
  },
  weekly: {
    label: "Weekly Reports",
    description: "Weekly summaries and forward-looking analysis",
    order: 2,
  },
  adhoc: {
    label: "Ad-Hoc Reports",
    description: "Special reports on key market events",
    order: 3,
  },
};

// Category badge styling for dense table
export const CATEGORY_BADGES: Record<
  MailingList["type"],
  { label: string; className: string }
> = {
  daily: {
    label: "DAILY",
    className: "bg-blue-900/50 text-blue-300",
  },
  weekly: {
    label: "WEEKLY",
    className: "bg-purple-900/50 text-purple-300",
  },
  monthly: {
    label: "MONTHLY",
    className: "bg-green-900/50 text-green-300",
  },
  adhoc: {
    label: "AD-HOC",
    className: "bg-amber-900/50 text-amber-300",
  },
};

// Subscription definitions with metadata
export const SUBSCRIPTION_DEFINITIONS: Omit<
  MailingListSubscription,
  "isSubscribed"
>[] = [
  // Daily Reports
  {
    audienceKey: "daily_us_earnings",
    id: "9",
    title: "Daily US Earnings Estimates",
    description: "Key US equity earnings before the market opens",
    category: "daily",
    deliveryTime: "0700 London / 0200 Central",
  },
  {
    audienceKey: "daily_economic_releases",
    id: "2",
    title: "Daily Economic Releases",
    description: "All key economic data releases for the day ahead",
    category: "daily",
    deliveryTime: "0700 London / 0200 Central",
  },
  {
    audienceKey: "daily_european_opening",
    id: "29",
    title: "Daily European Opening News",
    description: "European market-moving news ahead of the open",
    category: "daily",
    deliveryTime: "0700 London / 0200 Central",
  },
  {
    audienceKey: "daily_european_equity",
    id: "30",
    title: "Daily European Equity Opening News",
    description: "European equity stories and broker moves",
    category: "daily",
    deliveryTime: "0700 London / 0200 Central",
  },
  {
    audienceKey: "daily_us_opening",
    id: "31",
    title: "Daily US Opening News",
    description: "US market-moving news ahead of Wall Street",
    category: "daily",
    deliveryTime: "1400 London / 0800 Central",
  },
  {
    audienceKey: "daily_us_equity_opening",
    id: "32",
    title: "Daily US Equity Opening News",
    description: "Key US equity stories and broker moves",
    category: "daily",
    deliveryTime: "1400 London / 0800 Central",
  },
  {
    audienceKey: "bond_auction_previews",
    id: "33",
    title: "Bond Auction Previews",
    description: "Key bond auction information for Europe and the US",
    category: "daily",
    deliveryTime: "0800 London / 0200 Central",
  },
  {
    audienceKey: "us_market_wrap",
    id: "34",
    title: "US Market Wrap",
    description: "End of day summary after the US market close",
    category: "daily",
    deliveryTime: "After US close",
  },
  {
    audienceKey: "daily_asia_pac_opening",
    id: "37",
    title: "Daily Asia-Pac Opening News",
    description: "News from EU and US sessions for Asia-Pac preparation",
    category: "daily",
    deliveryTime: "2200 London / 1600 Central",
  },
  {
    audienceKey: "daily_us_conference_calendar",
    id: "40",
    title: "Daily US Conference Calendar",
    description: "Daily calendar of US equity conferences",
    category: "daily",
    deliveryTime: "US morning",
  },
  // Weekly Reports
  {
    audienceKey: "week_ahead",
    id: "10",
    title: "Week Ahead",
    description: "Forward-looking analysis for the week ahead",
    category: "weekly",
  },
  {
    audienceKey: "weekly_economic_calendar",
    id: "11",
    title: "Weekly Economic Calendar",
    description: "All economic releases for the coming week",
    category: "weekly",
  },
  {
    audienceKey: "weekly_us_earnings",
    id: "23",
    title: "Weekly US Earnings Estimates",
    description: "US earnings estimates for the coming week",
    category: "weekly",
  },
  // Ad-Hoc Reports
  {
    audienceKey: "adhoc_economic_analysis",
    id: "12",
    title: "Ad-Hoc Economic Analysis",
    description: "In-depth analysis on significant economic events",
    category: "adhoc",
  },
  {
    audienceKey: "calendar_key_events",
    id: "26",
    title: "Calendar of Key Events",
    description: "Major upcoming market events calendar",
    category: "adhoc",
  },
  {
    audienceKey: "options_expiry",
    id: "28",
    title: "Options Expiry Calendar",
    description: "Options expiration dates and details",
    category: "adhoc",
  },
  {
    audienceKey: "central_banks",
    id: "25",
    title: "Central Banks",
    description: "Central bank analysis and policy updates",
    category: "adhoc",
  },
];
